// HomeScreen.jsx
// Presents the LocalVoice homepage with guided actions and featured case studies.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { findIssueBySlug } from '../issues';
import AboutLocalVoiceDialog from '../components/AboutLocalVoiceDialog';

/**
 * HomeScreen
 * Combines the hero, featured case studies, action steps, and update signup prompts.
 */
export default function HomeScreen() {
  const [isAboutDialogOpen, setIsAboutDialogOpen] = useState(false);

  const handleFooterLinkClick = (event) => {
    event.preventDefault();
    setIsAboutDialogOpen(true);
  };

  const handleCloseAboutDialog = () => {
    setIsAboutDialogOpen(false);
  };

  return (
    <div className="min-h-screen bg-[#F8F9FB] text-slate-900">
      <main>
        <HeroSection />
        <WhatsWorkingSection />
        <FirstStepSection />
      </main>
      <FooterSection onAboutClick={handleFooterLinkClick} onFeedbackClick={handleFooterLinkClick} />
      <AboutLocalVoiceDialog open={isAboutDialogOpen} onClose={handleCloseAboutDialog} />
    </div>
  );
}

/**
 * SectionContainer
 * Wraps each section in the shared max-width layout and spacing rhythm.
 */
function SectionContainer({ children, className = '' }) {
  return <div className={`mx-auto w-full max-w-[60rem] px-6 sm:px-8 ${className}`}>{children}</div>;
}

/**
 * HeroSection
 * Introduces the mission with focused calls to explore Massachusetts issues.
 */
function HeroSection() {
  return (
    <section className="bg-white py-20 sm:py-24">
      <SectionContainer className="text-center">
        <h1 className="mx-auto max-w-3xl text-5xl font-extrabold leading-tight text-slate-900 sm:text-6xl [text-wrap:balance]">
          Your{' '}
          <span className="bg-gradient-to-r from-indigo-500 via-sky-400 to-emerald-400 bg-clip-text text-transparent">
            voice
          </span>{' '}
          can change Massachusetts
        </h1>
        <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-500 sm:text-xl">
          LocalVoice helps you learn how people across Massachusetts are taking action on issues that shape daily life
        </p>
        <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
          <PrimaryButton href="/ma/housing" label="🏘 Housing" />
          <PrimaryButton href="/ma/healthcare" label="🏥 Healthcare" />
          <PrimaryButton href="/ma/democracy" label="🗳 Democracy" />
        </div>
      </SectionContainer>
    </section>
  );
}

/**
 * WhatsWorkingSection
 * Highlights recent Massachusetts wins to inspire visitors.
 */
function createCaseStudy({ stateSlug, issueSlug, fallback }) {
  const issue = findIssueBySlug(stateSlug, issueSlug);
  const story = issue?.barrierStories?.[0];

  if (!issue || !story) {
    return fallback ?? null;
  }

  return {
    eyebrow: 'Case study',
    title: story.title ?? issue.title ?? `${stateSlug} ${issueSlug}`,
    summary: story.caption ?? story.takeaway ?? issue.summary ?? '',
    href: `/${issue.stateSlug}/${issue.issueSlug}`,
    imageUrl: story.imageUrl ?? null,
    imageAlt: story.imageAlt ?? story.title ?? '',
    imageSource: story.imageSource ?? '',
  };
}

function WhatsWorkingSection() {
  const featuredIssueConfigs = [
    { stateSlug: 'ma', issueSlug: 'housing' },
    { stateSlug: 'ma', issueSlug: 'healthcare' },
    {
      stateSlug: 'ma',
      issueSlug: 'democracy',
      fallback: {
        eyebrow: 'Case study',
        title: 'Democracy',
        summary: 'tbd',
        href: '/ma/democracy',
        imageUrl: null,
        imageAlt: '',
      },
    },
  ];

  const caseStudies = featuredIssueConfigs
    .map((config) => createCaseStudy(config))
    .filter(Boolean);

  return (
    <section className="bg-slate-100 py-20 sm:py-24">
      <SectionContainer>
        <div className="text-center">
          <h2 className="text-3xl font-extrabold text-slate-900">Change starts close to home</h2>
          <p className="mx-auto mt-4 max-w-3xl text-base text-slate-500">
            See how neighbors and local organizers have worked together to make change in their communities.
          </p>
        </div>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {caseStudies.map((item) => (
            <CaseStudyCard key={`${item.href}-${item.title}`} item={item} />
          ))}
        </div>
      </SectionContainer>
    </section>
  );
}

/**
 * CaseStudyCard
 * Displays a single case study with supporting summary and link.
 */
function CaseStudyCard({ item }) {
  const eyebrowClassName = [
    'text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600',
    item.imageUrl && item.imageSource ? 'mt-2' : null,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Link
      to={item.href}
      className="group flex flex-col overflow-hidden rounded-xl bg-white transition hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500"
      style={{ boxShadow: '0 4px 24px rgba(17, 24, 39, 0.06)' }}
    >
      {item.imageUrl ? (
        <img src={item.imageUrl} alt={item.imageAlt} className="h-36 w-full object-cover" />
      ) : (
        <div className="h-36 w-full bg-indigo-100" aria-hidden="true" />
      )}
      <div className="flex flex-1 flex-col p-6">
        {item.imageUrl && item.imageSource ? (
          <p className="text-xs text-slate-500">Photo by {item.imageSource}</p>
        ) : null}
        <p className={eyebrowClassName}>{item.eyebrow}</p>
        <h3 className="mt-2 text-base font-semibold text-slate-900">{item.title}</h3>
        <p className="mt-2 text-sm text-slate-700">{item.summary}</p>
        <span className="mt-4 inline-flex items-center text-sm font-semibold text-indigo-600 transition group-hover:underline">
          See how <span aria-hidden="true" className="ml-1">→</span>
        </span>
      </div>
    </Link>
  );
}

/**
 * FirstStepSection
 * Invites visitors to choose an issue with a purple action panel.
 */
function FirstStepSection() {
  return (
    <section className="py-20 sm:py-24">
      <SectionContainer>
        <div
          className="rounded-3xl bg-indigo-600 px-8 py-12 text-center text-white"
          style={{ boxShadow: '0 4px 24px rgba(17, 24, 39, 0.12)' }}
        >
          <h2 className="text-3xl font-bold [text-wrap:balance]">Every victory began with people showing up and connecting</h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-indigo-100">
            Pick the issue that matters most to you and learn about local groups leading change
          </p>
          <div className="mx-auto flex items-center">
            <ol className="mt-8 space-y-3 mx-auto text-center text-base sm:text-lg">
              <li className="flex items-center gap-3">
                <span className="mt-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-white text-sm font-semibold text-indigo-700">
                  1
                </span>
                <span>Pick an issue</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="mt-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-white text-sm font-semibold text-indigo-700">
                  2
                </span>
                <span>Learn about what's working</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="mt-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-white text-sm font-semibold text-indigo-700">
                  3
                </span>
                <span>Explore a group to learn more</span>
              </li>
            </ol>
          </div>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <PrimaryButton href="/ma/housing" label="🏘 Housing" invert />
            <PrimaryButton href="/ma/healthcare" label="🏥 Healthcare" invert />
            <PrimaryButton href="/ma/democracy" label="🗳 Democracy" invert />
          </div>
        </div>
      </SectionContainer>
    </section>
  );
}

/**
 * FooterSection
 * Shows the footer navigation with metadata and delegates link clicks to shared handlers.
 */
function FooterSection({ onAboutClick, onFeedbackClick }) {
  return (
    <footer className="bg-white py-12">
      <SectionContainer className="text-center">
        <nav aria-label="Footer" className="text-sm text-slate-600">
          <Link className="transition hover:text-indigo-600" to="/about" onClick={onAboutClick}>
            About LocalVoice
          </Link>
          <span className="mx-2 text-slate-300">•</span>
          <Link className="transition hover:text-indigo-600" to="/feedback" onClick={onFeedbackClick}>
            Give feedback
          </Link>
          {/* <span className="mx-2 text-slate-300">•</span> */}
          {/* <Link className="transition hover:text-indigo-600" to="/privacy">
            Privacy
          </Link> */}
        </nav>
        <p className="mt-4 text-xs text-slate-400">
          LocalVoice is a nonpartisan educational project. We do not take positions on legislation, candidates, or political
          parties.
        </p>
      </SectionContainer>
    </footer>
  );
}

/**
 * PrimaryButton
 * Provides the shared pill-shaped button style used across calls to action.
 */
function PrimaryButton({ href, label, invert = false }) {
  const baseClasses =
    'group inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-200';
  const colorClasses = invert
    ? 'bg-white text-indigo-600 shadow-lg shadow-indigo-900/20 hover:bg-indigo-100'
    : 'bg-indigo-600 text-white shadow-lg shadow-indigo-900/20 hover:bg-indigo-500';

  return (
    <Link to={href} className={`${baseClasses} ${colorClasses}`}>
      {label}
      <span aria-hidden="true" className="transition-transform duration-150 group-hover:translate-x-[2px]">
        →
      </span>
    </Link>
  );
}
